/**
 * Centralized Auth Context
 *
 * Single source of truth for authentication state across the entire app.
 * Replaces the previous pattern where multiple components each created their
 * own onAuthStateChange listeners, causing race conditions and redirect loops
 * on mobile devices.
 *
 * IMPORTANT: This is the ONLY place in the app that should call
 * supabase.auth.onAuthStateChange(). All other components should use useAuth().
 */

import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import { clearAllCoachingDrafts, setCoachingDraftAccount } from '@/components/human-coaching/recordingDrafts';
import { isOAuthCallbackUrl, isOAuthErrorCallback, getOAuthError, clearOAuthParams, OAuthError } from '@/lib/oauth-callback';
import {
  cleanupDemoStateIfNeeded,
  isDemoMode,
} from '@/lib/demoUtils';
import { log } from '@/lib/logger';

interface AuthContextType {
  /** The current Supabase user, or null if not authenticated */
  user: User | null;
  /** The current Supabase session, or null if not authenticated */
  session: Session | null;
  /** True while the initial auth check is in progress */
  isLoading: boolean;
  /** True while processing an OAuth callback (tokens in URL) */
  isProcessingOAuth: boolean;
  /** Convenience boolean: true when user is non-null */
  isAuthenticated: boolean;
  /**
   * True when auth state has fully settled and it is safe to make
   * redirect decisions (e.g., "redirect to /auth if no user").
   * False while loading, processing OAuth, or during the brief
   * stabilization window after token refresh.
   */
  isAuthReady: boolean;
  /** True when user arrived via a password reset link and needs to set a new password */
  isPasswordRecovery: boolean;
  /** Call after user has set their new password to clear the recovery state */
  clearPasswordRecovery: () => void;
  /** OAuth error details if the OAuth callback failed (e.g., Google rejected the code exchange) */
  oauthError: OAuthError | null;
  /** Clear the stored OAuth error after it has been displayed */
  clearOAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(() =>
    // If the URL has OAuth error params (no access_token), skip the processing wait entirely
    isOAuthCallbackUrl() && !isOAuthErrorCallback()
  );
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [oauthError, setOAuthError] = useState<OAuthError | null>(() =>
    isOAuthErrorCallback() ? getOAuthError() : null
  );

  // Ref to track isProcessingOAuth inside the effect closure without re-running it
  const isProcessingOAuthRef = useRef(isProcessingOAuth);
  isProcessingOAuthRef.current = isProcessingOAuth;

  // One-shot guard: once isAuthReady is true, it never resets to false.
  // Prevents token refresh events from re-opening the redirect window on mobile.
  const isAuthReadyRef = useRef(false);

  // Single onAuthStateChange listener for the entire app.
  // Dependency array is empty - this runs once on mount only.
  useEffect(() => {
    let mounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;

      log.debug('Auth state change (central)', { event, hasSession: !!newSession });

      // Notify Chrome extension content script of token change so it can
      // sync fresh credentials immediately (instead of waiting for 30s poll)
      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        window.postMessage({ type: 'NYNCH_AUTH_UPDATE' }, window.location.origin);
      }

      // Clear all cached query data on sign-out to prevent stale/cross-user data leaks
      setCoachingDraftAccount(newSession?.user.id ?? null);
      if (event === 'SIGNED_OUT') {
        queryClient.clear();
        // Remove account-scoped coaching drafts even when no coaching page is mounted.
        try {
          Object.keys(localStorage).filter(key => key.startsWith('nynch-coaching:')).forEach(key => localStorage.removeItem(key));
        } catch { /* Browser storage can be unavailable. */ }
        void clearAllCoachingDrafts().catch(() => undefined);
        if ('serviceWorker' in navigator) void navigator.serviceWorker.getRegistration('/').then(async registration => {
          const subscription = await registration?.pushManager.getSubscription();
          await subscription?.unsubscribe();
        }).catch(() => undefined);
      }

      // Detect password recovery flow — user clicked a reset link in their email
      if (event === 'PASSWORD_RECOVERY') {
        log.info('Password recovery session detected');
        setIsPasswordRecovery(true);
      }

      setSession(newSession);
      setUser(newSession?.user ?? null);
      setIsLoading(false);

      // If we were processing an OAuth callback and now have a user, mark it done
      if (newSession?.user && isProcessingOAuthRef.current) {
        log.info('OAuth callback completed, user authenticated');
        setIsProcessingOAuth(false);
        clearOAuthParams();
      }
    });

    // Initial session check
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      if (!mounted) return;

      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setCoachingDraftAccount(currentSession?.user.id ?? null);
      setIsLoading(false);

      if (currentSession?.user && isProcessingOAuthRef.current) {
        log.info('Existing session found during OAuth callback');
        setIsProcessingOAuth(false);
        clearOAuthParams();
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  // OAuth callback timeout - prevent infinite loading if OAuth fails silently
  useEffect(() => {
    if (isProcessingOAuth) {
      const timeout = setTimeout(() => {
        log.warn('OAuth callback timeout - clearing processing state');
        setIsProcessingOAuth(false);
        clearOAuthParams();
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [isProcessingOAuth]);

  // Auth readiness: safe to make redirect decisions once loading and OAuth are done.
  // isAuthReady is ONE-SHOT — once true, it stays true. This prevents token refresh
  // events (which briefly toggle isLoading) from resetting the flag and re-triggering
  // redirect effects in Index.tsx, which caused infinite redirect loops on mobile.
  // The 150ms delay lets auth state settle on initial load.
  useEffect(() => {
    // Already ready — never go back to false
    if (isAuthReadyRef.current) return;

    if (!isLoading && !isProcessingOAuth) {
      const timer = setTimeout(() => {
        if (!isAuthReadyRef.current) {
          isAuthReadyRef.current = true;
          setIsAuthReady(true);
        }
      }, 150);
      return () => clearTimeout(timer);
    }
    // No else { setIsAuthReady(false) } — the initial useState(false) handles the
    // "not yet ready" case; we never reset to false after first settling.
  }, [isLoading, isProcessingOAuth]);

  // Clean up lingering demo state when a real user session is confirmed.
  // Pass the active user's email so the cleanup never wipes the demo user's
  // own session token by mistake when they are the active session.
  useEffect(() => {
    if (!isLoading && user) {
      cleanupDemoStateIfNeeded(user.email ?? null);
      log.info('Real auth session detected, demo mode cleanup triggered');
    }
  }, [isLoading, user]);

  // Set Sentry user context so errors can be attributed to specific users.
  // Clear on sign-out so subsequent anonymous errors aren't misattributed.
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email ?? undefined });
      Sentry.setTag('demo_mode', isDemoMode() ? 'true' : 'false');
    } else if (!isLoading) {
      Sentry.setUser(null);
    }
  }, [user, isLoading]);

  const clearPasswordRecovery = () => setIsPasswordRecovery(false);
  const clearOAuthError = () => setOAuthError(null);

  // Clean up OAuth error params from URL on mount so they don't persist across refreshes
  useEffect(() => {
    if (oauthError) {
      log.warn('OAuth callback error detected', { error: oauthError.error, code: oauthError.errorCode });
      clearOAuthParams();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isProcessingOAuth,
    isAuthenticated: !!user,
    isAuthReady,
    isPasswordRecovery,
    clearPasswordRecovery,
    oauthError,
    clearOAuthError };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access the centralized auth state.
 * Must be used within an AuthProvider.
 *
 * Usage:
 * ```tsx
 * const { user, isLoading, isAuthReady } = useAuth();
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalAuth(): AuthContextType | null {
  return useContext(AuthContext) ?? null;
}
